import React, { useState, useCallback, useEffect, memo } from 'react';
import { Calendar, Clock, User, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import ClientDayView from './ClientDayView';
import ClientDayViewMobile from './ClientDayViewMobile';
import ClientWeekView from './ClientWeekView';
import ClientWeekViewMobile from './ClientWeekViewMobile';
import ClientMonthView from './ClientMonthView';

const ClientAgendaView = memo(({ client, appointments, employees, db, onClose, hasPermission, onRefresh }) => {
  const [currentView, setCurrentView] = useState('day'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [clientStats, setClientStats] = useState({
    regularEmployees: [],
    monthlyHoursAvg: 0,
    startDate: null,
    totalAppointments: 0
  });

  // Détection mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fonctions de navigation temporelle
  const handlePrevious = useCallback(() => {
    const newDate = new Date(currentDate);
    switch (currentView) {
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
    }
    setCurrentDate(newDate);
  }, [currentDate, currentView]);

  const handleNext = useCallback(() => {
    const newDate = new Date(currentDate);
    switch (currentView) {
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
    }
    setCurrentDate(newDate);
  }, [currentDate, currentView]);

  const handleToday = useCallback(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCurrentDate(d);
  }, []);

  // Calculer les statistiques du client
  useEffect(() => {
    if (!appointments || appointments.length === 0) return;

    // Calculer les intervenants habituels (ceux qui ont le plus de rendez-vous)
    // Regrouper par NOM d'employé pour éviter les doublons d'IDs
    const employeeCountsByName = {};
    let totalHours = 0;
    let firstAppointmentDate = null;

    appointments.forEach(apt => {
      const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);

      // Compter les rendez-vous par nom d'employé (au lieu de l'ID)
      if (apt.employeeName) {
        employeeCountsByName[apt.employeeName] = (employeeCountsByName[apt.employeeName] || 0) + 1;
      }

      // Calculer les heures totales
      if (apt.startTime && apt.endTime) {
        const start = apt.startTime.split(':');
        const end = apt.endTime.split(':');
        const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
        const endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
        totalHours += (endMinutes - startMinutes) / 60;
      }

      // Trouver le premier rendez-vous
      if (!firstAppointmentDate || aptDate < firstAppointmentDate) {
        firstAppointmentDate = aptDate;
      }
    });

    // Trier les employés par nombre de rendez-vous et prendre les 3 premiers
    const regularEmployees = Object.entries(employeeCountsByName)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name, count], index) => ({
        id: `employee-${index}`,
        name: name,
        count: count
      }));

    // Calculer la moyenne d'heures mensuelles
    let monthlyHoursAvg = 0;
    if (firstAppointmentDate) {
      const monthsDiff = Math.max(1, Math.floor((new Date() - firstAppointmentDate) / (1000 * 60 * 60 * 24 * 30)));
      monthlyHoursAvg = Math.round((totalHours / monthsDiff) * 10) / 10;
    }

    setClientStats({
      regularEmployees,
      monthlyHoursAvg,
      startDate: firstAppointmentDate,
      totalAppointments: appointments.length
    });
  }, [appointments]);

  // Formater la date selon la vue
  const formatDateRange = useCallback(() => {
    switch (currentView) {
      case 'day':
        return currentDate.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      case 'week':
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Lundi
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Dimanche

        return `${startOfWeek.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      case 'month':
        return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      default:
        return '';
    }
  }, [currentDate, currentView]);

  // Mémoïser les fonctions de formatage pour les vues enfants pour éviter les re-rendus inutiles
  const childFormatDate = useCallback((date) => date.toLocaleDateString('fr-FR'), []);
  const childFormatTime = useCallback((time) => time, []);
  const childAdjustForTimezone = useCallback((date) => date, []);




  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
        {/* En-tête principal - responsive */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
            >
              <ChevronLeft size={20} className="dark:text-gray-300" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                Agenda de {client.firstName} {client.lastName}
              </h1>
            </div>
          </div>

          <div className="flex items-center justify-between md:justify-end space-x-2">
            <button
              onClick={handleToday}
              className="px-3 py-2 text-xs md:text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors flex-shrink-0"
            >
              Aujourd'hui
            </button>
            <div className="flex items-center space-x-1">
              <button
                onClick={handlePrevious}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              >
                <ChevronLeft size={16} className="dark:text-gray-300" />
              </button>
              <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px] md:min-w-[200px] text-center truncate">
                {formatDateRange()}
              </span>
              <button
                onClick={handleNext}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              >
                <ChevronRight size={16} className="dark:text-gray-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Section des statistiques - responsive */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 md:p-4 mb-4 border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {/* Intervenants habituels */}
            <div className="flex items-start space-x-2 md:space-x-3">
              <div className="p-1.5 md:p-2 bg-blue-100 dark:bg-blue-900 rounded-lg flex-shrink-0">
                <Users size={16} className="text-blue-600 dark:text-blue-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Intervenants habituels</p>
                {clientStats.regularEmployees.length > 0 ? (
                  <div className="space-y-0.5">
                    {clientStats.regularEmployees.map((emp, index) => (
                      <div key={emp.id} className="text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {index + 1}. {emp.name} <span className="text-xs text-gray-500 dark:text-gray-400">({emp.count} rdv)</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Aucun intervenant assigné</p>
                )}
              </div>
            </div>

            {/* Heures mensuelles moyennes */}
            <div className="flex items-start space-x-2 md:space-x-3">
              <div className="p-1.5 md:p-2 bg-green-100 dark:bg-green-900 rounded-lg flex-shrink-0">
                <Clock size={16} className="text-green-600 dark:text-green-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Heures mensuelles moyennes</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {clientStats.monthlyHoursAvg}h
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Basé sur {clientStats.totalAppointments} rendez-vous
                </p>
              </div>
            </div>

            {/* Date de début du suivi */}
            <div className="flex items-start space-x-2 md:space-x-3">
              <div className="p-1.5 md:p-2 bg-purple-100 dark:bg-purple-900 rounded-lg flex-shrink-0">
                <Calendar size={16} className="text-purple-600 dark:text-purple-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Bénéficiaire depuis</p>
                <p className="text-sm md:text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                  {clientStats.startDate
                    ? clientStats.startDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
                    : 'Non défini'}
                </p>
                {clientStats.startDate && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Il y a {Math.floor((new Date() - clientStats.startDate) / (1000 * 60 * 60 * 24 * 30))} mois
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation par onglets - responsive */}
        <div className="flex space-x-1 overflow-x-auto pb-1">
          {[
            { key: 'day', label: 'Jour', icon: Clock },
            { key: 'week', label: 'Semaine', icon: Calendar },
            { key: 'month', label: 'Mois', icon: Calendar }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setCurrentView(key)}
              className={`flex items-center space-x-1 md:space-x-2 px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${currentView === key
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
            >
              <Icon size={14} className="md:size-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Corps de la vue */}
      <div className="flex-1 overflow-hidden">
        {currentView === 'day' && (
          isMobile ? (
            <ClientDayViewMobile
              db={db}
              client={client}
              currentDate={currentDate}
              formatDate={childFormatDate}
              formatTime={childFormatTime}
              hasPermission={hasPermission}
            />
          ) : (
            <ClientDayView
              db={db}
              client={client}
              currentDate={currentDate}
              formatDate={childFormatDate}
              formatTime={childFormatTime}
              hasPermission={hasPermission}
            />
          )
        )}

        {currentView === 'week' && (
          isMobile ? (
            <ClientWeekViewMobile
              db={db}
              client={client}
              currentDate={currentDate}
              formatDate={childFormatDate}
              formatTime={childFormatTime}
              hasPermission={hasPermission}
            />
          ) : (
            <ClientWeekView
              db={db}
              client={client}
              currentDate={currentDate}
              onClose={onClose}
              hasPermission={hasPermission}
            />
          )
        )}

        {currentView === 'month' && (
          isMobile ? (
            <ClientMonthViewMobile
              db={db}
              client={client}
              currentDate={currentDate}
              onClose={onClose}
              formatDate={childFormatDate}
              formatTime={childFormatTime}
              adjustForTimezone={childAdjustForTimezone}
              appointments={appointments}
              employees={employees}
              hasPermission={hasPermission}
            />
          ) : (
            <ClientMonthView
              db={db}
              client={client}
              currentDate={currentDate}
              onClose={onClose}
              formatDate={childFormatDate}
              formatTime={childFormatTime}
              adjustForTimezone={childAdjustForTimezone}
              appointments={appointments}
              employees={employees}
              hasPermission={hasPermission}
              fetchAppointments={onRefresh}
            />
          )
        )}
      </div>

    </div>
  );
});

// Nom d'affichage pour le débogage React DevTools
ClientAgendaView.displayName = 'ClientAgendaView';

export default ClientAgendaView;
